import React, { useContext, useEffect, useState } from "react";
import { IonLabel, IonSegment, IonSegmentButton } from "@ionic/react";
import { DestajosContext } from "../../context/DestajosContext";
import { ProyectosContext } from "../../context/ProyectosContext";
import { EntradasContext } from "../../context/EntradasContext";
import { AppConfigContext } from "../../context/AppConfigContext";
import { AvanceModeContext } from "../../context/AvanceModeContext";
import { AuthContext } from "../../context/AuthContext";
import BitacoraInput from "../bitacora/BitacoraInput";
import Entradas from "../bitacora/Entradas";
import AvancePickerMobile from "./AvancePickerMobile";
import ConceptoProgressBar from "../global/ConceptoProgressBar";
import DestajoHistorial from "../obra/DestajoHistorial";
import ItemDetail from "./ItemDetail";
import { getValue } from "../../utils";

const DestajoDetail = ({ idProyecto, destajo, callback }) => {
  const [tab, setTab] = useState("details");

  const { proyecto } = useContext(ProyectosContext);
  const { postEntrada } = useContext(EntradasContext);
  const { user } = useContext(AuthContext);
  const appconfig = useContext(AppConfigContext);
  const { mode, inputType } = useContext(AvanceModeContext);
  const {
    getSingleDestajo,
    postSingleDestajo,
    setPropertySingleDestajo,
    postAvanceDestajo,
  } = useContext(DestajosContext);

  const { permiso, idPermiso, modo_avance } = proyecto || {};

  const canEditProgress =
    ["admin", "supervisor", "contratista"].includes(permiso) ||
    [3, 6].includes(parseInt(idPermiso));

  // Destajos only take avance when the project tracks progress at the destajo level.
  const showAvancePicker =
    (modo_avance || "destajo") === "destajo" &&
    getValue(appconfig, "progress") !== "money" &&
    canEditProgress;

  const concepto =
    Array.isArray(destajo.conceptos) && destajo.conceptos.length > 0
      ? destajo.conceptos[0]
      : null;
  const cantidad = concepto ? Number(concepto.cantidad) : NaN;
  const unidad = concepto && concepto.unidad ? concepto.unidad : "";
  const acumulado =
    !isNaN(cantidad) && cantidad > 0
      ? Math.round((parseFloat(destajo.progreso) || 0) * cantidad * 100) / 100
      : null;

  // Amount mode applies only when the global toggle is "amount" and this destajo
  // has a concepto with a positive cantidad to measure against (mirrors DestajoTabla).
  const unitMode = mode === "amount" && !!concepto && cantidad > 0;

  const postAvance = (cont, isManual) => {
    postAvanceDestajo(
      destajo.idDestajo,
      cont,
      idProyecto,
      () => {
        fetchDestajo();
        if (callback) callback();
      },
      isManual,
      unitMode ? concepto.idConcepto : undefined,
      unitMode
    );
  };

  // Real-vs-programado bar — same one used in PartidaDetail / ProgresoPartidaChart.
  const realPct =
    getValue(appconfig, "progress") === "money"
      ? parseInt(destajo.ejecutado * 100)
      : parseInt(destajo.progreso * 100);
  const programadoPct = parseInt(Number(destajo.programado) * 100);
  const diff = programadoPct - realPct;
  let programadoClass = "programado-gradiente";
  if (diff > 10) programadoClass = "diferencial-gradiente";
  else if (diff >= 5) programadoClass = "pagos-gradiente";

  useEffect(() => {
    fetchDestajo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDestajo = () => {
    getSingleDestajo(idProyecto, destajo.idDestajo);
  };

  // Destajo comments are linked to the partida (so they surface in the partida's
  // comments) and to the concepto when the destajo has one.
  const postComentario = (message, files) => {
    postEntrada(
      idProyecto,
      {
        message,
        files,
        idPartida: destajo.idPartida,
        ...(concepto ? { idConcepto: concepto.idConcepto } : {}),
      },
      fetchDestajo
    );
  };

  const renderConceptoTotals = () => {
    if (!concepto || isNaN(cantidad)) return null;
    return (
      <div className="d-flex justify-content-between border rounded p-2 mb-3 bg-light">
        <div>
          <div className="text-muted" style={{ fontSize: 11 }}>
            CANTIDAD
          </div>
          <div className="fw-semibold">
            {cantidad} {unidad}
          </div>
        </div>
        {acumulado != null && (
          <div className="text-end">
            <div className="text-muted" style={{ fontSize: 11 }}>
              AVANCE
            </div>
            <div className="fw-semibold">
              {acumulado} {unidad}{" "}
              <span className="text-muted">
                ({parseInt((parseFloat(destajo.progreso) || 0) * 100)}%)
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDetails = () => {
    if (tab === "details") {
      return (
        <>
          {renderConceptoTotals()}
          <ItemDetail
            id="idDestajo"
            item={destajo}
            proyecto={proyecto}
            itemLabel="Destajo"
            callback={callback}
            saveItem={postSingleDestajo}
            modifier={setPropertySingleDestajo}
            progressBar={
              <ConceptoProgressBar
                idPartida={destajo.idDestajo}
                programado={programadoPct}
                real={realPct}
                handleClick={() => {}}
                programadoClass={programadoClass}
                height={16}
                showLabels
              />
            }
            avancePicker={
              showAvancePicker ? (
                <AvancePickerMobile
                  permitirExcedente
                  progreso={
                    unitMode ? acumulado : parseInt(destajo.progreso * 100)
                  }
                  setCantidad={postAvance}
                  intervalo={1}
                  limite={unitMode ? cantidad : 100}
                  inputType={inputType}
                  suffix={unitMode ? unidad : "%"}
                />
              ) : null
            }
          />
        </>
      );
    }
  };

  const renderHistorial = () => {
    if (tab === "history") {
      return (
        <DestajoHistorial
          idProyecto={idProyecto}
          idDestajo={destajo.idDestajo}
          concepto={concepto}
          progreso={destajo.progreso}
        />
      );
    }
  };

  const renderComments = () => {
    if (tab === "comments") {
      return (
        <div
          className="d-flex flex-column"
          style={{ height: "calc(85vh - 110px)" }}
        >
          <div className="flex-grow-1 overflow-auto">
            {Array.isArray(destajo.Entradas) && destajo.Entradas.length > 0 ? (
              <Entradas
                user={user}
                height="auto"
                idProyecto={idProyecto}
                entradas={destajo.Entradas}
              />
            ) : (
              <p className="text-muted small">No hay comentarios creados</p>
            )}
          </div>
          <div className="flex-shrink-0">
            <BitacoraInput modifier={postComentario} />
          </div>
        </div>
      );
    }
  };

  return (
    <div
      className="container-fluid d-flex flex-column"
      style={{ height: "100%", paddingTop: 16 }}
    >
      <IonSegment value={tab} className="mb-3 flex-shrink-0">
        <IonSegmentButton onClick={() => setTab("details")} value="details">
          <IonLabel>Detalles</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton onClick={() => setTab("comments")} value="comments">
          <IonLabel>Comentarios</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton onClick={() => setTab("history")} value="history">
          <IonLabel>Historial</IonLabel>
        </IonSegmentButton>
      </IonSegment>

      <div className="flex-grow-1 overflow-auto pb-3">
        {renderDetails()}
        {renderHistorial()}
      </div>
      {renderComments()}
    </div>
  );
};

export default DestajoDetail;
